// ============================================================
// WitnessesSection.tsx
// Multi-row witness CRUD. Unlike the other sections (which are
// nested fields on the Case row), witnesses live in a separate
// table — so this section talks to its own API endpoints and uses
// per-witness debounced PATCH via useWitnessAutoSave.
//
// Stage 2 redesign (approved mockup casevo-case-sections-batch1.html §1):
// a LIST section in the shared card language — one card per witness
// (contact on the left, statement on the right; pencil + ⋯ menu), per-card
// edit with Cancel/Save, an "Add another witness" tile, the at-a-glance
// strip and the shared section footer. DATA LOGIC IS UNCHANGED: the same
// handleFieldChange (optimistic store + scheduleWitnessSave), the 5-field
// address write, createWitnessApi + base capture, ConfirmDialogue delete
// with cancelWitnessSave, the per-row Yjs statement editor + snapshot, the
// row-scoped fieldKeys, and flushWitnessAutoSave before Mark complete.
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import { useLoadPhase, SKELETON_BAR } from '../../hooks/useLoadPhase'
import { useCasevoStore } from '../../store/useCasevoStore'
import { Witness } from '../../types/case.types'
import FormField from '../shared/FormField'
import PhoneLink from '../shared/PhoneLink'
import AddressLookup from '../shared/AddressLookup'
import RichTextEditor from '../shared/RichTextEditor'
import { useCollabUser } from '../../hooks/useCollabUser'
import DOMPurify from 'dompurify'
import {
  createWitnessApi,
  deleteWitnessApi,
  getWitnessMobileChangesApi,
  updateWitnessApi,
} from '../../api/casevoApi'
import {
  scheduleWitnessSave,
  cancelWitnessSave,
  flushWitnessAutoSave,
} from '../../hooks/useWitnessAutoSave'
import { getWitnessBaseUpdatedAt, setWitnessBaseUpdatedAt, setWitnessBaseValues } from '../../hooks/saveBaseVersion'
import { useEsignPilot } from '../../hooks/useEsignPilot'
import { MOBILE_FORMAT_ERROR, formatUkMobile } from '../../utils/mobileNumber'
import { errorText } from '../billing/billingUi'
import { MOBILE_LABEL, MobileChangedHint, MobileInput, MobileValue, mobileE164Of, mobileInputError } from '../clientInfo/MobileNumberField'
import { broadcastFieldEdits } from '../../hooks/usePresence'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import CardPresence from '../shared/CardPresence'
import { flushLiveDocSnapshots, saveDidFail, SAVE_FAILED_MSG } from '../shared/liveDocFlush'
import { CARD, CARD_EDITING, BTN_SEC, BTN_PRI, SM } from '../clientInfo/ClientInfoSections'
import { IcEdit, IcCheck, inpCls, FGRID } from '../clientInfo/clientInfoRegistry'
import { MENU_SURFACE, MenuItem, MenuIcon, MenuSep } from '../shared/menuUi'
import {
  KIcon, SectionHead, Glance, GlanceDash, SectionFooter, Empty, AddressBlock, LiveDocEdit, LiveNote, Clamp, LiveDocView,
  CHIP, CHIP_N, LINK, htmlHasText, wordCount,
} from '../case/sections/sectionKit'

// Each editable field on a witness. We list these explicitly (rather
// than iterating over Object.keys) so we can give each a nice label.
// Add a new field here AND in the Witness type to wire it through.
type WitnessTextField =
  | 'name'
  | 'addressLine1'
  | 'addressLine2'
  | 'addressTown'
  | 'addressCounty'
  | 'addressPostcode'
  | 'phone'
  | 'email'
const TEXT_FIELDS: WitnessTextField[] = ['name', 'addressLine1', 'addressLine2', 'addressTown', 'addressCounty', 'addressPostcode', 'phone', 'email']
type Addr = { line1: string; line2: string; town: string; county: string; postcode: string }

// Avatar colour per witness (stable by id) — the mockup's violet / sky / green.
const AV_COLOURS = ['#6a4c9c', '#2f6aa3', '#2d6a4f', '#8a6212', '#a3412f']
const avColour = (id: string): string => {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return AV_COLOURS[h % AV_COLOURS.length]
}
const initialsOf = (name: string): string => {
  const parts = name.replace(/^(mr|mrs|ms|miss|dr)\.?\s+/i, '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
}
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const shortDay = (iso: string): string => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : `${d.getDate()} ${MONTHS[d.getMonth()]}`
}
const relTime = (iso: string): string => {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const m = Math.round((Date.now() - t) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`
  const d = Math.round(h / 24)
  return d < 30 ? `${d} day${d === 1 ? '' : 's'} ago` : shortDay(iso)
}

// ── one witness card ─────────────────────────────────────────
interface WitnessCardProps {
  witness: Witness
  index: number
  isDeleting: boolean
  collabUser: ReturnType<typeof useCollabUser>
  onFieldChange: (witness: Witness, field: WitnessTextField, value: string) => void
  onAddress: (witness: Witness, addr: Addr) => void
  onStatementSnapshot: (witnessId: string, html: string) => void
  onRemove: (witness: Witness) => void
  onRestore: (witness: Witness, changes: Partial<Witness>) => void
}
const WitnessCard: React.FC<WitnessCardProps> = ({
  witness, index, isDeleting, collabUser, onFieldChange, onAddress, onStatementSnapshot, onRemove, onRestore,
}) => {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [snapshot, setSnapshot] = useState<Partial<Witness>>({})
  // B4c (D4) The mobile for e-signature codes (pilot only). NOT autosaved: a
  // draft, sent on the card's Save (validated first; audited server-side);
  // Cancel discards it.
  const pilot = useEsignPilot()
  const [mobileDraft, setMobileDraft] = useState('')
  const [mobileErr, setMobileErr] = useState<string | undefined>(undefined)
  const [mobileRev, setMobileRev] = useState(0)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const w = witness
  const id = `wit-${w.id}`
  const docName = `witness:${w.id}:statement`
  const hasStatement = htmlHasText(w.statement)

  // Close the ⋯ menu on outside press / Esc.
  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e: MouseEvent): void => { if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false) }
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') { e.stopPropagation(); setMenuOpen(false) } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true) }
  }, [menuOpen])

  const open = (): void => {
    setSnapshot(Object.fromEntries(TEXT_FIELDS.map((f) => [f, w[f]])) as Partial<Witness>)
    setMobileDraft(formatUkMobile(w.mobile)); setMobileErr(undefined)
    setMenuOpen(false)
    setEditing(true)
  }
  // Cancel restores the 8 contact fields (the statement is a live document).
  const cancel = (): void => {
    const changes: Partial<Witness> = {}
    for (const f of TEXT_FIELDS) if (f in snapshot && snapshot[f] !== w[f]) (changes as Record<string, unknown>)[f] = snapshot[f]
    if (Object.keys(changes).length) onRestore(w, changes)
    setMobileErr(undefined)
    setSaveFailed(false)
    setEditing(false)
  }
  const save = async (): Promise<void> => {
    // (D4) A bad mobile stops the Save here, said under the field.
    const mobileBad = pilot ? mobileInputError(mobileDraft) : null
    if (mobileBad) { setMobileErr(mobileBad); return }
    setSaving(true)
    setSaveFailed(false)
    // Statement snapshot first (→ scheduleWitnessSave), so Save persists it NOW.
    try { await flushLiveDocSnapshots(); await flushWitnessAutoSave() } finally { setSaving(false) }
    if (saveDidFail()) { setSaveFailed(true); return }
    if (pilot && mobileE164Of(mobileDraft) !== (w.mobile ?? null)) {
      setSaving(true)
      try {
        const base = getWitnessBaseUpdatedAt(w.id)
        const updated = await updateWitnessApi(w.id, { mobile: mobileDraft.trim(), ...(base ? { baseUpdatedAt: base } : {}) })
        setWitnessBaseUpdatedAt(w.id, updated.updatedAt)
        const store = useCasevoStore.getState()
        const row = (store.witnesses[w.caseId] ?? []).find((x) => x.id === w.id) ?? w
        store.updateWitness({ ...row, mobile: updated.mobile ?? null, updatedAt: updated.updatedAt })
        setMobileRev((n) => n + 1)
      } catch (e) {
        const resp = (e as { response?: { status?: number; data?: { field?: string; error?: string } } }).response
        if (resp?.status === 400 && resp.data?.field === 'mobile') setMobileErr(resp.data.error ?? MOBILE_FORMAT_ERROR)
        else setMobileErr(resp?.status === 409
          ? 'Someone else changed this witness just now. Cancel, then edit again to see their changes.'
          : errorText(e, 'Couldn’t save the mobile number. Please try again.'))
        return
      } finally { setSaving(false) }
    }
    setEditing(false)
  }

  const addrLines = [w.addressLine1, w.addressLine2, [w.addressTown, w.addressCounty].filter((s) => s.trim()).join(', ')].map((s) => s.trim()).filter(Boolean)
  const pc = w.addressPostcode.trim()
  const words = wordCount(w.statement)
  const text = (f: WitnessTextField, label: string, opts: { type?: string; placeholder?: string; full?: boolean; mono?: boolean } = {}): React.ReactNode => (
    <div className={opts.full ? 'sm:col-span-2' : ''}>
      <FormField variant="info" label={label} fieldKey={`witness.${w.id}.${f}`} htmlFor={`${id}-${f}`}>
        <input
          id={`${id}-${f}`}
          type={opts.type ?? 'text'}
          value={w[f]}
          onChange={(e) => onFieldChange(w, f, e.target.value)}
          placeholder={opts.placeholder}
          className={`${inpCls(false)} ${opts.mono ? 'font-mono tracking-[0.04em]' : ''}`}
          disabled={isDeleting}
        />
      </FormField>
    </div>
  )
  const rowIcon = 'text-casevo-text-soft mt-[2px] inline-flex'

  return (
    <section aria-label={w.name.trim() || `Witness ${index + 1}`} className={`${CARD} group/card md:col-span-2 flex flex-col transition-[border-color,box-shadow] ${editing ? `${CARD_EDITING} overflow-hidden` : ''}`}>
      {/* header (mockup .sch.wh) */}
      <div className="flex items-center gap-[11px] pt-[14px] pr-4 pb-3 pl-[18px] flex-wrap">
        <span aria-hidden="true" className="w-9 h-9 rounded-full grid place-items-center text-[13px] font-bold text-white shrink-0" style={{ background: avColour(w.id) }}>{initialsOf(w.name)}</span>
        <div className="min-w-0">
          <h3 className="m-0 text-[15px] font-semibold tracking-[-0.005em] leading-[1.2] text-casevo-forest">{w.name.trim() || <span className="text-casevo-text-soft italic font-medium">Unnamed witness</span>}</h3>
          <div className="text-[12px] text-casevo-text-soft mt-px">Witness {index + 1}{shortDay(w.createdAt) ? ` · added ${shortDay(w.createdAt)}` : ''}</div>
        </div>
        <div className="ml-auto flex gap-1 items-center">
          {/* Who's editing this witness (shared presence — visible while viewing). */}
          <CardPresence match={(k) => k.startsWith(`witness.${w.id}.`)} className="mr-1" />
          {hasStatement
            ? <span className={`${CHIP} mr-1`}><KIcon name="quote" size={12} />Statement taken</span>
            : <span className={`${CHIP_N} mr-1`}>No statement yet</span>}
          {editing ? (
            <span className="text-[12.5px] text-casevo-text-soft">Editing</span>
          ) : (
            <>
              <button
                type="button"
                onClick={open}
                disabled={isDeleting}
                title="Edit witness"
                aria-label={`Edit ${w.name.trim() || `witness ${index + 1}`}`}
                className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand dark:hover:text-[#a8e6c5] transition-[opacity,background-color,color] focus:outline-none casevo-focus md:opacity-0 md:group-hover/card:opacity-100 md:group-focus-within/card:opacity-100"
              >
                {IcEdit(15)}
              </button>
              <div ref={menuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label="Witness actions"
                  className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-input transition-colors focus:outline-none casevo-focus"
                >
                  <KIcon name="dots" size={15} />
                </button>
                {menuOpen && (
                  <div role="menu" className={`${MENU_SURFACE} !w-[196px] absolute right-0 top-[34px] z-20`}>
                    <MenuItem icon={<MenuIcon name="edit" />} label="Edit witness" onClick={open} />
                    {w.email.trim() && (
                      <MenuItem icon={<KIcon name="mail" />} label="Email witness" onClick={() => { setMenuOpen(false); window.location.href = `mailto:${w.email.trim()}` }} />
                    )}
                    <MenuSep />
                    <MenuItem icon={<MenuIcon name="trash" />} label={isDeleting ? 'Removing…' : 'Remove witness'} tone="danger" disabled={isDeleting} onClick={() => { setMenuOpen(false); onRemove(w) }} />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <>
          <div className="pt-[2px] px-[18px] pb-4">
            <div className={FGRID}>
              {text('name', 'Name', { full: true })}
              {/* Address lookup — onSelect/onClear both write all 5 fields in
                  ONE updateWitness + scheduleWitnessSave (handleAddressLookupUpdate). */}
              <div className="sm:col-span-2">
                <FormField variant="info" label="Search address" fieldKey={`witness.${w.id}.address-search`} htmlFor={`${id}-search`}>
                  <AddressLookup
                    variant="info"
                    inputId={`${id}-search`}
                    value={
                      w.addressLine1 || w.addressLine2 || w.addressTown || w.addressCounty || w.addressPostcode
                        ? { line1: w.addressLine1, line2: w.addressLine2, town: w.addressTown, county: w.addressCounty, postcode: w.addressPostcode }
                        : null
                    }
                    onSelect={(addr) => onAddress(w, addr)}
                    onClear={() => onAddress(w, { line1: '', line2: '', town: '', county: '', postcode: '' })}
                  />
                </FormField>
              </div>
              {text('addressLine1', 'Address line 1')}
              {text('addressLine2', 'Address line 2')}
              {text('addressTown', 'Town')}
              {text('addressCounty', 'County')}
              {text('addressPostcode', 'Postcode', { placeholder: 'LS6 3AB', mono: true })}
              <div className="hidden sm:block" />
              {text('phone', 'Phone', { type: 'tel', placeholder: '07700 900000' })}
              {text('email', 'Email', { type: 'email', placeholder: 'name@example.com' })}
              {pilot && (
                <MobileInput id={`${id}-mobile`} value={mobileDraft} error={mobileErr} disabled={isDeleting || saving}
                  onChange={(v) => { setMobileDraft(v); setMobileErr(undefined) }} />
              )}
              {/* Statement — Yjs collaborative rich-text, PER ROW. Doc
                  `witness:<witnessId>:statement`; keyed by the doc name. The
                  debounced HTML snapshot mirrors to the `statement` column via
                  handleStatementSnapshot. Same props as before. */}
              <div className="sm:col-span-2">
                <FormField variant="info" label="Statement" fieldKey={`witness.${w.id}.statement`}>
                  <LiveDocEdit>
                    {(onStatus) => (
                      <RichTextEditor
                        key={docName}
                        collabDocName={docName}
                        collabToken={() => localStorage.getItem('casevo_token') ?? ''}
                        initialHtml={DOMPurify.sanitize(w.statement ?? '')}
                        onSnapshot={(html) => onStatementSnapshot(w.id, html)}
                        placeholder="Enter the witness statement…"
                        collabUser={collabUser}
                        onCollabStatus={onStatus}
                      />
                    )}
                  </LiveDocEdit>
                </FormField>
              </div>
            </div>
            <LiveNote>The statement is a live document: everyone sees each other’s typing, and it isn’t part of Cancel.</LiveNote>
          </div>
          <div className="flex items-center gap-2 justify-end flex-wrap py-[10px] px-[14px] bg-casevo-input border-t border-casevo-border-soft">
            <span className="mr-auto text-[12px] text-casevo-text-soft flex items-center gap-1.5">
              <i className="w-[7px] h-[7px] rounded-full bg-casevo-mid" aria-hidden="true" />Saves as you type · Cancel undoes the contact fields
            </span>
            {saveFailed && <span role="alert" className="text-[12px] text-casevo-danger">{SAVE_FAILED_MSG}</span>}
            <button type="button" className={`${BTN_SEC} ${SM} !text-casevo-danger`} onClick={() => onRemove(w)} disabled={isDeleting || saving}>
              <MenuIcon name="trash" size={13} />{isDeleting ? 'Removing…' : 'Remove'}
            </button>
            <button type="button" className={`${BTN_SEC} ${SM}`} onClick={cancel} disabled={saving}>Cancel</button>
            <button type="button" className={`${BTN_PRI} ${SM}`} onClick={() => { void save() }} disabled={saving}>{IcCheck(13)}{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </>
      ) : (
        // view body (mockup .wbody / .cl / .wst)
        <div className="grid grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)] gap-[22px] pt-1 px-[18px] pb-[18px]">
          <div className="flex flex-col gap-[11px] min-w-0">
            <div className="flex gap-[10px] items-start text-[14px] leading-[1.45]">
              <span className={rowIcon}><KIcon name="phone" size={15} /></span>
              {w.phone.trim() ? <PhoneLink value={w.phone} className={LINK} /> : <Empty onAdd={open} />}
            </div>
            <div className="flex gap-[10px] items-start text-[14px] leading-[1.45] min-w-0">
              <span className={rowIcon}><KIcon name="mail" size={15} /></span>
              {w.email.trim() ? <a className={`${LINK} [overflow-wrap:anywhere]`} href={`mailto:${w.email.trim()}`}>{w.email}</a> : <Empty onAdd={open} />}
            </div>
            {pilot && (
              <div className="flex gap-[10px] items-start text-[14px] leading-[1.45] min-w-0">
                <span className={rowIcon}><KIcon name="phone" size={15} /></span>
                <div className="min-w-0" data-testid="witness-mobile">
                  <div className="text-[11px] font-bold tracking-[0.1em] uppercase text-casevo-text-soft">{MOBILE_LABEL}</div>
                  <MobileValue e164={w.mobile} className={LINK} empty={<Empty onAdd={open} />} />
                  <MobileChangedHint load={() => getWitnessMobileChangesApi(w.id)} refreshKey={`${w.id}:${mobileRev}`} />
                </div>
              </div>
            )}
            <div className="flex gap-[10px] items-start text-[14px] leading-[1.45]">
              <span className={rowIcon}><KIcon name="map" size={15} /></span>
              {addrLines.length || pc ? <AddressBlock lines={addrLines} postcode={pc || undefined} /> : <Empty onAdd={open} />}
            </div>
          </div>
          <div className="min-w-0 md:border-l md:border-casevo-border-soft md:pl-[22px]">
            <div className="text-[11px] font-bold tracking-[0.1em] uppercase text-casevo-text-soft mb-[6px] flex items-center gap-2">
              Statement
              {hasStatement && <span className="font-medium tracking-normal normal-case">· {words} word{words === 1 ? '' : 's'} · updated {relTime(w.updatedAt)}</span>}
            </div>
            {/* LIVE read-only statement (saved copy until synced / if collab is
                down; the "No statement" box when empty). */}
            <Clamp moreLabel="Read full statement">
              <LiveDocView
                docName={docName}
                html={w.statement}
                onSnapshot={(html) => onStatementSnapshot(w.id, html)}
                collabUser={collabUser}
                empty={
              <div className="border-[1.5px] border-dashed border-casevo-border rounded-[10px] p-4 text-center text-[13px] text-casevo-text-soft">
                <b className="block text-casevo-forest text-[13.5px] mb-[2px] font-semibold">No statement recorded</b>
                Take or paste the witness’s account. It’s co-edited live, like case notes.
                <div className="mt-[10px]">
                  <button type="button" className={`${BTN_SEC} ${SM}`} onClick={open} disabled={isDeleting}><KIcon name="plus" size={13} />Add statement</button>
                </div>
              </div>
                }
              />
            </Clamp>
          </div>
        </div>
      )}
    </section>
  )
}

const WitnessesSection: React.FC = () => {
  const { currentCase, witnesses, addWitness, updateWitness, removeWitness } = useCasevoStore()
  // Local UI state for the "Add witness" button's spinner so the user
  // gets feedback while POST is in flight. Failures roll the spinner
  // back and surface a small inline error message.
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  // Pending-delete state — keyed by witnessId so removing one card
  // doesn't lock the others.
  const [deletingId, setDeletingId] = useState<string | null>(null)
  // Inline error for delete failures.
  const [actionError, setActionError] = useState<string | null>(null)
  // The witness the user asked to remove — drives the ConfirmDialogue.
  const [confirmDeleteWitness, setConfirmDeleteWitness] = useState<Witness | null>(null)
  // Collab identity for the Yjs carets others see on the statement editors.
  // Hook must run before the early return below.
  const collabUser = useCollabUser()
  // ★ NO LAYOUT SHIFT: witnesses load as a satellite of the case open. Until
  // they arrive, show the glance + card placeholders (invisible for 250ms) —
  // never the "No witnesses yet" empty card, which was wrong AND the wrong size.
  const witnessesLoaded = !currentCase || witnesses[currentCase.id] !== undefined
  const witnessPhase = useLoadPhase(witnessesLoaded)
  if (!currentCase) return null
  const list = witnesses[currentCase.id] ?? []
  // Field-edit handler — runs on every keystroke.
  // 1) Optimistic store update so the UI stays responsive.
  // 2) Schedule the debounced PATCH with just the changed field.
  const handleFieldChange = (
    witness: Witness,
    field: WitnessTextField | 'statement',
    value: string,
  ) => {
    updateWitness({ ...witness, [field]: value })
    scheduleWitnessSave(witness.id, { [field]: value })
  }
  // Statement HTML-mirror snapshot (Yjs). Reads the FRESHEST row from the
  // store so a snapshot landing after another field changed can't
  // stale-overwrite that field.
  const handleStatementSnapshot = (witnessId: string, html: string): void => {
    const store = useCasevoStore.getState()
    const row = (store.witnesses[currentCase.id] ?? []).find((w) => w.id === witnessId)
    if (!row) return
    store.updateWitness({ ...row, statement: html })
    scheduleWitnessSave(witnessId, { statement: html })
  }
  // Multi-field address update — AddressLookup onSelect / onClear. MUST set
  // all 5 address fields in ONE updateWitness + ONE scheduleWitnessSave.
  const handleAddressLookupUpdate = (witness: Witness, addr: Addr) => {
    const next = {
      addressLine1: addr.line1,
      addressLine2: addr.line2,
      addressTown: addr.town,
      addressCounty: addr.county,
      addressPostcode: addr.postcode,
    }
    // Live row (the lookup resolves async — the render-time `witness` may be stale).
    const row = (useCasevoStore.getState().witnesses[currentCase.id] ?? []).find((w) => w.id === witness.id) ?? witness
    updateWitness({ ...row, ...next })
    scheduleWitnessSave(witness.id, next)
    // Programmatic multi-field write (no per-field blur) — broadcast the row's
    // 5 address fieldKeys for live sync.
    broadcastFieldEdits([
      `witness.${witness.id}.addressLine1`,
      `witness.${witness.id}.addressLine2`,
      `witness.${witness.id}.addressTown`,
      `witness.${witness.id}.addressCounty`,
      `witness.${witness.id}.addressPostcode`,
    ])
  }
  // Card Cancel — restore the captured contact fields through the SAME
  // per-row path (one optimistic update + one merged debounced PATCH), then
  // broadcast them (programmatic change, like the address lookup).
  const handleRestore = (witness: Witness, changes: Partial<Witness>): void => {
    const row = (useCasevoStore.getState().witnesses[currentCase.id] ?? []).find((w) => w.id === witness.id) ?? witness
    updateWitness({ ...row, ...changes })
    scheduleWitnessSave(witness.id, changes)
    broadcastFieldEdits(Object.keys(changes).map((f) => `witness.${witness.id}.${f}`))
  }
  const handleAdd = async () => {
    setAddError(null)
    setAdding(true)
    try {
      const created = await createWitnessApi(currentCase.id)
      addWitness(currentCase.id, created)
      // Capture the new row's server updatedAt + column VALUES so an edit right
      // after creation is guarded AND the column-aware 409 recovery has a base.
      setWitnessBaseUpdatedAt(created.id, created.updatedAt)
      setWitnessBaseValues(created.id, created as unknown as Record<string, unknown>)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add witness'
      setAddError(message)
    } finally {
      setAdding(false)
    }
  }
  // Remove opens the app's ConfirmDialogue; the delete runs in handleConfirmDelete.
  const handleRemove = (witness: Witness): void => {
    setConfirmDeleteWitness(witness)
  }
  const handleConfirmDelete = async (): Promise<void> => {
    const witness = confirmDeleteWitness
    if (!witness) return
    setConfirmDeleteWitness(null)
    setActionError(null)
    setDeletingId(witness.id)
    // Drop any pending debounced PATCH for this row.
    cancelWitnessSave(witness.id)
    try {
      await deleteWitnessApi(witness.id)
      removeWitness(currentCase.id, witness.id)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Witnesses] Failed to delete witness:', err)
      setActionError('Could not remove witness. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  // ── at-a-glance (mockup witnesses glance) ──
  const taken = list.filter((w) => htmlHasText(w.statement)).length
  const byPhone = list.filter((w) => w.phone.trim()).length
  const byEmail = list.filter((w) => w.email.trim()).length
  const latest = list.reduce<Witness | null>((acc, w) => (!acc || w.updatedAt > acc.updatedAt ? w : acc), null)

  return (
    <div className="text-[14px] leading-[1.5] text-casevo-forest">
      <SectionHead
        title="Witnesses"
        sub="People who saw the accident, and their statements."
        actions={
          <button type="button" className={BTN_SEC} onClick={() => { void handleAdd() }} disabled={adding}>
            <KIcon name="plus" size={15} />{adding ? 'Adding…' : 'Add witness'}
          </button>
        }
      />
      {actionError && (
        <div role="alert" className="mb-[14px] flex items-center gap-3 px-[14px] py-[11px] rounded-[12px] border border-[#f5c2bb] bg-[#fbe9e6] text-[#c23b2c] dark:border-[rgba(243,160,148,0.35)] dark:bg-[rgba(243,160,148,0.14)] dark:text-[#f3a094] text-[13.5px]">
          <span className="flex-1">{actionError}</span>
          <button type="button" onClick={() => setActionError(null)} aria-label="Dismiss error" className="shrink-0 hover:opacity-70 focus:outline-none casevo-focus rounded-[3px]">
            <MenuIcon name="x" size={15} />
          </button>
        </div>
      )}
      {addError && <p role="alert" className="mb-[14px] text-[12.5px] text-casevo-danger">{addError}</p>}
      {!witnessesLoaded ? (
        <div aria-hidden="true" className={witnessPhase === 'wait' ? 'invisible' : ''}>
          <Glance cells={[
            { icon: 'users', label: 'Witnesses', value: <GlanceDash />, sub: 'On this case' },
            { icon: 'quote', label: 'Statements', value: <GlanceDash />, sub: ' ' },
            { icon: 'phone', label: 'Reachable', value: <GlanceDash />, sub: ' ' },
            { icon: 'time', label: 'Last update', value: <GlanceDash />, sub: ' ' },
          ]} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[14px] items-start">
            {[0, 1].map((i) => (
              <div key={i} className={`${CARD} p-[18px] flex flex-col gap-3`}>
                <span className={`${SKELETON_BAR} h-4 w-40`} />
                <span className={`${SKELETON_BAR} h-3 w-56`} />
                <span className={`${SKELETON_BAR} h-3 w-48`} />
                <span className="block h-24 rounded-[10px] bg-casevo-border-soft motion-safe:animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      ) : list.length > 0 && (
        <Glance cells={[
          { icon: 'users', label: 'Witnesses', value: <span className="tabular-nums">{list.length}</span>, sub: 'On this case' },
          { icon: 'quote', label: 'Statements', value: <span className="tabular-nums">{taken} of {list.length}</span>, sub: taken === list.length ? 'All taken' : `${list.length - taken} outstanding` },
          { icon: 'phone', label: 'Reachable', value: <span className="tabular-nums">{byPhone} by phone</span>, sub: `${byEmail} by email` },
          { icon: 'time', label: 'Last update', value: latest ? relTime(latest.updatedAt) : '—', sub: latest ? (latest.name.trim() || `Witness ${list.indexOf(latest) + 1}`) : '' },
        ]} />
      )}
      {!witnessesLoaded ? null : list.length === 0 ? (
        // Empty state — a short content-height card with the add CTA.
        <div className={`${CARD} flex flex-col items-center text-center px-6 py-8`}>
          <span className="w-10 h-10 rounded-[11px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] grid place-items-center mb-3"><KIcon name="users" size={18} /></span>
          <b className="text-[15px] font-semibold text-casevo-forest">No witnesses on this case yet</b>
          <p className="text-[13px] text-casevo-text-soft mt-1 mb-4 max-w-sm">Add witness statements to capture supporting accounts.</p>
          <button type="button" className={BTN_PRI} onClick={() => { void handleAdd() }} disabled={adding}>
            <KIcon name="plus" size={15} />{adding ? 'Adding…' : 'Add witness'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[14px] items-start">
          {list.map((witness, index) => (
            <WitnessCard
              key={witness.id}
              witness={witness}
              index={index}
              isDeleting={deletingId === witness.id}
              collabUser={collabUser}
              onFieldChange={handleFieldChange}
              onAddress={handleAddressLookupUpdate}
              onStatementSnapshot={handleStatementSnapshot}
              onRemove={handleRemove}
              onRestore={handleRestore}
            />
          ))}
          {/* mockup .addw — dashed "Add another witness" tile */}
          <button
            type="button"
            onClick={() => { void handleAdd() }}
            disabled={adding}
            className="md:col-span-2 flex items-center justify-center gap-[9px] p-[15px] border-[1.5px] border-dashed border-casevo-border rounded-[14px] text-casevo-brand dark:text-[#8fd6b0] font-semibold text-[14px] bg-transparent hover:border-casevo-mid hover:bg-casevo-mint/30 transition-colors focus:outline-none casevo-focus disabled:opacity-60"
          >
            <KIcon name="plus" size={16} />{adding ? 'Adding…' : 'Add another witness'}
          </button>
        </div>
      )}
      {/* Sequential-flow footer — always shown (zero witnesses is a valid
          completion state). Flushes pending per-witness PATCHes first. */}
      <SectionFooter
        sectionKey="witnesses"
        errors={[]}
        beforeComplete={flushWitnessAutoSave}
        readySub="No required fields. A case can have no witnesses at all."
      />
      <ConfirmDialogue
        isOpen={confirmDeleteWitness !== null}
        title="Remove witness?"
        message={
          confirmDeleteWitness && confirmDeleteWitness.name.trim()
            ? `"${confirmDeleteWitness.name}" will be permanently removed. This cannot be undone.`
            : 'This witness will be permanently removed. This cannot be undone.'
        }
        confirmLabel="Remove"
        onConfirm={() => { void handleConfirmDelete() }}
        onCancel={() => setConfirmDeleteWitness(null)}
      />
    </div>
  )
}
export default WitnessesSection
